import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import api from '@/api/mutator/custom-instance';

import type { AgentList, AgentVersionList } from '@/gen-api/models';
import {
  useListAgents,
  useListAgents2,
  useListPublicAgents2,
  useListHybridAgents,
  getGetAgent2QueryKey,
  getGetPublicAgent2QueryKey,
  useGetAgent2,
  useGetPublicAgent2,
  useListAgentVersions,
  useListAgentVersions3,
  useListAgentVersions4,
  useListAgentVersions2,
  useActivateAgentVersion,
  useActivateAgentVersion4,
  useGetAgent3,
  useGetPublicAgent3,
  useHireAgent,
  useHirePublicAgent,
  useArchiveAgent,
  useArchivePublicAgent,
  useProbationAgent,
  useEndProbationAgent,
  useGetAgent,
  useGetPublicAgent,
  useTenantVersionsFinalisePartialUpdate,
  usePublicVersionsFinalisePartialUpdate,
  useTenantVersionsRejectPartialUpdate,
  usePublicVersionsRejectPartialUpdate,
} from '@/gen-api/api';
import { useAuthStore } from '@/store/authStore';
import { refreshRoleAffectedQueries } from './rosterRefresh';
import { publishRosterChange } from './rosterEvents';
import { getListAgentsQueryKey, getListHybridAgentsQueryKey, getTeamGraphRetrieveQueryKey } from '@/gen-api/api';

// ─── Helpers ────────────────────────────────────────────────────────────────

function normalizeAgentArray(response: unknown): AgentList[] {
  if (Array.isArray(response)) return response;
  if (response && typeof response === 'object' && 'results' in response) {
    const wrapped = response as { results?: unknown };
    if (Array.isArray(wrapped.results)) return wrapped.results;
  }
  return [];
}

function normalizeVersionArray(response: unknown): AgentVersionList[] {
  if (Array.isArray(response)) return response;
  if (response && typeof response === 'object' && 'results' in response) {
    const wrapped = response as { results?: unknown };
    if (Array.isArray(wrapped.results)) return wrapped.results;
  }
  return [];
}

// ─── Agent list ─────────────────────────────────────────────────────────────

export function useTeammateAgents() {
  const { currentWorkspace } = useAuthStore();
  const isVigilantAIWorkspace = currentWorkspace?.slug === 'vigilantai';

  const {
    data: hybridResponse = [],
    isLoading: isLoadingHybrid,
    refetch: refetchHybrid,
  } = useListHybridAgents(undefined, {
    query: { enabled: isVigilantAIWorkspace },
  });

  const {
    data: tenantResponse = [],
    isLoading: isLoadingTenant,
    refetch: refetchTenant,
  } = useListAgents(undefined, {
    query: { enabled: !isVigilantAIWorkspace },
  });

  const agents = useMemo(
    () => normalizeAgentArray(isVigilantAIWorkspace ? hybridResponse : tenantResponse),
    [isVigilantAIWorkspace, hybridResponse, tenantResponse]
  );

  return {
    agents,
    isLoading: isVigilantAIWorkspace ? isLoadingHybrid : isLoadingTenant,
    refetch: isVigilantAIWorkspace ? refetchHybrid : refetchTenant,
    isVigilantAIWorkspace,
  };
}

// ─── Single agent detail ────────────────────────────────────────────────────

export function useTeammateAgent(agentId: string | undefined, isPublic?: boolean) {
  const { currentWorkspace } = useAuthStore();
  const isVigilantAIWorkspace = currentWorkspace?.slug === 'vigilantai';

  // When isPublic is unknown and workspace is vigilantai, try tenant first
  // then fall back to public on error (shared agents live in the public schema).
  const shouldFallbackToPublic = isPublic === undefined && isVigilantAIWorkspace;

  const tenantQuery = useGetAgent2(agentId || '', {
    query: {
      enabled: !!agentId && !isPublic,
      retry: shouldFallbackToPublic ? false : undefined,
    },
  });

  const tenantFailed = shouldFallbackToPublic && tenantQuery.isError;

  const publicQuery = useGetPublicAgent2(agentId || '', {
    query: { enabled: !!agentId && (!!isPublic || tenantFailed) },
  });

  const resolvedPublic = isPublic || tenantFailed;
  const query = resolvedPublic ? publicQuery : tenantQuery;

  return {
    agent: query.data,
    isLoading: query.isLoading || (tenantQuery.isLoading && shouldFallbackToPublic),
    refetch: query.refetch,
  };
}

// ─── Versions ───────────────────────────────────────────────────────────────

/**
 * True when a version save was rejected because a newer version appeared since
 * the editor loaded (backend `StaleBaseVersionError`, HTTP 409). Used to refresh
 * the editor and warn the user instead of silently reverting the prompt.
 */
export function isStaleVersionConflict(error: unknown): boolean {
  const status =
    (error as { response?: { status?: number } })?.response?.status ?? (error as { status?: number })?.status;
  return status === 409;
}

export function useTeammateVersions(agentId: string | undefined, isPublic?: boolean) {
  const { data: publicVersions = [], isLoading: isLoadingPublic } = useListAgentVersions(agentId || '', {
    query: { enabled: !!agentId && !!isPublic },
  });

  const { data: tenantVersions = [], isLoading: isLoadingTenant } = useListAgentVersions3(agentId || '', {
    query: { enabled: !!agentId && !isPublic },
  });

  const versions = useMemo(
    () => normalizeVersionArray(isPublic ? publicVersions : tenantVersions),
    [isPublic, publicVersions, tenantVersions]
  );

  return {
    versions,
    isLoading: isPublic ? isLoadingPublic : isLoadingTenant,
  };
}

// ─── Mutations ──────────────────────────────────────────────────────────────

export function useTeammateMutations() {
  const queryClient = useQueryClient();

  const invalidateAgentQueries = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: getListAgentsQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getListHybridAgentsQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getTeamGraphRetrieveQueryKey() }),
    ]);

  const invalidateVersionQueries = (agentId: string, isPublic?: boolean) => {
    const prefix = isPublic ? 'public' : 'tenant';
    queryClient.invalidateQueries({
      queryKey: [`${import.meta.env.VITE_API_URL}/api/v1/${prefix}/${agentId}/versions/`],
    });
  };

  // Create tenant agent
  const createAgent = useListAgents2({
    mutation: {
      onSuccess: () => invalidateAgentQueries(),
    },
  });

  // Create public agent (shared across workspaces)
  const createPublicAgent = useListPublicAgents2({
    mutation: {
      onSuccess: () => invalidateAgentQueries(),
    },
  });

  // Update tenant agent (PATCH)
  const updateTenantAgent = useGetAgent3({
    mutation: {
      onMutate: () => ({ workspaceId: useAuthStore.getState().currentWorkspace?.id }),
      onSuccess: async (data, variables, context) => {
        const activeWorkspaceId = useAuthStore.getState().currentWorkspace?.id;
        if (!context?.workspaceId || context.workspaceId !== activeWorkspaceId) return;
        queryClient.setQueryData(getGetAgent2QueryKey(variables.id), data);
        if (variables.data?.role_classification !== undefined) {
          publishRosterChange(context.workspaceId);
          await refreshRoleAffectedQueries(
            queryClient,
            import.meta.env.VITE_API_URL,
            context.workspaceId,
            activeWorkspaceId
          );
        } else {
          await invalidateAgentQueries();
        }
      },
    },
  });

  // Update public agent (PATCH)
  const updatePublicAgent = useGetPublicAgent3({
    mutation: {
      onMutate: () => ({ workspaceId: useAuthStore.getState().currentWorkspace?.id }),
      onSuccess: async (data, variables, context) => {
        const activeWorkspaceId = useAuthStore.getState().currentWorkspace?.id;
        if (!context?.workspaceId || context.workspaceId !== activeWorkspaceId) return;
        queryClient.setQueryData(getGetPublicAgent2QueryKey(variables.id), data);
        if (variables.data?.role_classification !== undefined) {
          publishRosterChange(context.workspaceId);
          await refreshRoleAffectedQueries(
            queryClient,
            import.meta.env.VITE_API_URL,
            context.workspaceId,
            activeWorkspaceId
          );
        } else {
          await invalidateAgentQueries();
        }
      },
    },
  });

  // Hire tenant agent
  const hire = useHireAgent({
    mutation: {
      onSuccess: () => invalidateAgentQueries(),
    },
  });

  // Hire public agent
  const hirePublic = useHirePublicAgent({
    mutation: {
      onSuccess: () => invalidateAgentQueries(),
    },
  });

  // Archive tenant agent
  const archive = useArchiveAgent({
    mutation: {
      onSuccess: () => invalidateAgentQueries(),
    },
  });

  // Archive public agent
  const archivePublic = useArchivePublicAgent({
    mutation: {
      onSuccess: () => invalidateAgentQueries(),
    },
  });

  // Place agent on probation
  const probation = useProbationAgent({
    mutation: {
      onSuccess: () => invalidateAgentQueries(),
    },
  });

  // End agent probation
  const endProbation = useEndProbationAgent({
    mutation: {
      onSuccess: () => invalidateAgentQueries(),
    },
  });

  // Delete tenant agent
  const deleteTenant = useGetAgent({
    mutation: {
      onSuccess: () => invalidateAgentQueries(),
    },
  });

  // Delete public agent
  const deletePublic = useGetPublicAgent({
    mutation: {
      onSuccess: () => invalidateAgentQueries(),
    },
  });

  // Activate version (tenant)
  const activateTenantVersion = useActivateAgentVersion4({
    mutation: {
      onSuccess: (_data, variables) => {
        invalidateAgentQueries();
        invalidateVersionQueries(variables.agentId);
      },
    },
  });

  // Activate version (public)
  const activatePublicVersion = useActivateAgentVersion({
    mutation: {
      onSuccess: (_data, variables) => {
        invalidateAgentQueries();
        invalidateVersionQueries(variables.agentId, true);
      },
    },
  });

  // Finalise version (tenant).
  // NOTE: must hit PATCH .../versions/{id}/finalise/ (approve in place), NOT the
  // DELETE destroy route. The mislabeled `finaliseAgentVersion` operation maps to
  // DELETE (see core_agents/openapi.py), which would hard-delete the draft.
  const finalise = useTenantVersionsFinalisePartialUpdate({
    mutation: {
      onSuccess: (_data, variables) => {
        invalidateAgentQueries();
        invalidateVersionQueries(variables.agentId);
      },
    },
  });

  // Finalise version (public/global teammates).
  // Public agents live in the public schema, so the tenant finalise route
  // 404s for them ("Agent version not found" — VIG-1329). This hits the
  // dedicated public endpoint, which approves in place, rejects superseded
  // drafts, and busts the backend public-agents cache.
  const finalisePublic = usePublicVersionsFinalisePartialUpdate({
    mutation: {
      onSuccess: (_data, variables) => {
        invalidateAgentQueries();
        invalidateVersionQueries(variables.agentId, true);
      },
    },
  });

  // Reject (dismiss) a pending draft — keeps the version row for audit.
  // Tenant and public agents have separate routes (same split as finalise).
  const rejectTenantVersion = useTenantVersionsRejectPartialUpdate({
    mutation: {
      onSuccess: (_data, variables) => {
        invalidateAgentQueries();
        invalidateVersionQueries(variables.agentId);
      },
    },
  });

  const rejectPublicVersion = usePublicVersionsRejectPartialUpdate({
    mutation: {
      onSuccess: (_data, variables) => {
        invalidateAgentQueries();
        invalidateVersionQueries(variables.agentId, true);
      },
    },
  });

  // Create version (tenant)
  const createTenantVersion = useListAgentVersions4({
    mutation: {
      onSuccess: (_data, variables) => {
        invalidateAgentQueries();
        invalidateVersionQueries(variables.agentId);
      },
      // Stale-version conflict (VIG-xxxx): a newer version appeared since the
      // editor loaded. Refresh the version list so the editor snaps to the
      // latest instead of holding the stale one the save was refused for.
      onError: (error, variables) => {
        if (isStaleVersionConflict(error)) invalidateVersionQueries(variables.agentId);
      },
    },
  });

  // Create version (public)
  const createPublicVersion = useListAgentVersions2({
    mutation: {
      onSuccess: (_data, variables) => {
        invalidateAgentQueries();
        invalidateVersionQueries(variables.agentId, true);
      },
      onError: (error, variables) => {
        if (isStaleVersionConflict(error)) invalidateVersionQueries(variables.agentId, true);
      },
    },
  });

  return {
    createAgent,
    createPublicAgent,
    updateTenantAgent,
    updatePublicAgent,
    hire,
    hirePublic,
    archive,
    archivePublic,
    probation,
    endProbation,
    deleteTenant,
    deletePublic,
    activateTenantVersion,
    activatePublicVersion,
    finalise,
    finalisePublic,
    rejectTenantVersion,
    rejectPublicVersion,
    createTenantVersion,
    createPublicVersion,
    invalidateAgentQueries,
    invalidateVersionQueries,
  };
}

// ─── Prompt generation (standalone, not a hook) ─────────────────────────────

export interface GeneratePromptAttachment {
  file_id: number;
  is_important?: boolean;
}

export interface GeneratePromptMemory {
  memory_id: string;
  is_important?: boolean;
}

export interface GeneratePromptTrainingDebtItem {
  id?: string;
  name: string;
  description?: string;
}

export interface GeneratePromptFields {
  role_name: string;
  primary_mission?: string;
  north_star_goal?: string;
  voice_hint?: string;
  response_verbosity?: 'low' | 'medium' | 'high';
  key_responsibilities?: string;
  regulatory_basis?: string;
  extra_instructions?: string;
  tools?: Record<string, boolean>;
  /**
   * Optional: when set, the backend anchors generation on this agent's
   * active version so version-over-version diffs stay minimal. Omit when
   * creating a brand-new agent.
   */
  agent_id?: string;
  /**
   * Current knowledge-document state the new version will have. The backend
   * resolves each file's name + summary from its UploadedFile row.
   */
  attachments?: GeneratePromptAttachment[];
  /** MCP server UUIDs the new version will have enabled. */
  mcp_server_ids?: string[];
  /** Training-debt items (name + description) the new version will carry. */
  training_debt?: GeneratePromptTrainingDebtItem[];
  /**
   * Current memory-link state the new version will have. Tenant-only —
   * public agents have no memory primitive (plan §3) and the backend
   * silently ignores this field for them.
   */
  memories?: GeneratePromptMemory[];
}

// Pre-creation compliance verdict (VIG-1074). Returned alongside the
// generated prompt so the UI can gate save (BLOCK/AMEND/EXPLAIN) or surface
// a non-blocking warning (WARN). ALLOW → no visible surface.
export type CompliancePromptAction = 'ALLOW' | 'WARN' | 'BLOCK' | 'AMEND' | 'EXPLAIN';

export interface CompliancePromptResult {
  action: CompliancePromptAction;
  rationale: string | null;
  /** Compliance agent's suggested rewrite. Informational only — never auto-applied. */
  amendment: string | null;
  resource_violations: Record<string, unknown> | null;
  /** True for BLOCK / AMEND / EXPLAIN — UI should disable save. */
  is_blocking: boolean;
  /** True iff the compliance check itself errored; treat as ALLOW. */
  error: boolean;
}

export interface GenerateSystemPromptResult {
  prompt: string;
  compliance: CompliancePromptResult;
  /**
   * LLM-generated human-readable description of what changed relative to the
   * most recently created version (or an initial summary when there is none).
   * `null` if compliance blocked generation or the LLM failed (fail-open).
   * The frontend is responsible for forwarding this to the save endpoint so
   * it gets persisted with the new version.
   */
  changelog: string | null;
}

// Default compliance verdict used when the backend response is malformed or
// the endpoint is pre-VIG-1074. Keeps callers simple — they always have a
// `compliance` object to render against.
const DEFAULT_COMPLIANCE_ALLOW: CompliancePromptResult = {
  action: 'ALLOW',
  rationale: null,
  amendment: null,
  resource_violations: null,
  is_blocking: false,
  error: false,
};

// ─── Per-field AI enhancement (VIG-1309) ─────────────────────────────────────
// The compiled prompt is rendered deterministically on the backend; LLM
// assistance is opt-in and per-field. The enhance endpoint reads the sibling
// ontology fields + attached knowledge document summaries as background
// context, but returns a replacement for ONLY the targeted field. Nothing is
// persisted — the caller shows a before/after diff for Accept / Edit / Reject.

export type EnhanceableFieldKey =
  | 'primary_mission'
  | 'north_star_goal'
  | 'voice_hint'
  | 'key_responsibilities'
  | 'regulatory_basis'
  | 'extra_instructions';

export interface EnhanceFieldRequest {
  field: EnhanceableFieldKey;
  /** The field's current text (required, non-blank). */
  text: string;
  role_name?: string;
  /**
   * All current ontology values from the live Studio form (NOT the DB), so
   * unsaved edits and not-yet-created teammates are reflected in the
   * read-only background context.
   */
  ontology?: Record<string, string>;
  /** Attached knowledge docs; the backend resolves name + summary. */
  attachments?: GeneratePromptAttachment[];
}

export async function enhanceOntologyField(request: EnhanceFieldRequest): Promise<string> {
  const response = await api.post('/api/v1/tenant/enhance-field/', request);
  return response.data?.enhanced_text ?? '';
}

export async function generateSystemPrompt(fields: GeneratePromptFields): Promise<GenerateSystemPromptResult> {
  const response = await api.post('/api/v1/tenant/generate-prompt/', fields);
  const data = response.data ?? {};
  const compliance: CompliancePromptResult = data.compliance
    ? {
        action: (data.compliance.action ?? 'ALLOW') as CompliancePromptAction,
        rationale: data.compliance.rationale ?? null,
        amendment: data.compliance.amendment ?? null,
        resource_violations: data.compliance.resource_violations ?? null,
        is_blocking: !!data.compliance.is_blocking,
        error: !!data.compliance.error,
      }
    : DEFAULT_COMPLIANCE_ALLOW;
  return {
    prompt: data.generated_prompt ?? '',
    compliance,
    changelog: typeof data.changelog === 'string' && data.changelog.trim().length > 0 ? data.changelog : null,
  };
}
